import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Copy, 
  Gift, 
  Users, 
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Coins,
  Share,
  WhatsappLogo,
  TelegramLogo,
  Link as LinkIcon
} from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";

export default function ReferralsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardTimeframe, setLeaderboardTimeframe] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [infoRes, historyRes, withdrawalsRes, leaderboardRes] = await Promise.all([
        apiClient.get("/referrals/info"),
        apiClient.get("/referrals/history"),
        apiClient.get("/referrals/withdrawals"),
        apiClient.get(`/referrals/leaderboard?timeframe=${leaderboardTimeframe}`)
      ]);
      setReferralInfo(infoRes.data);
      setReferralHistory(historyRes.data);
      setWithdrawalHistory(withdrawalsRes.data);
      setLeaderboard(leaderboardRes.data?.entries || []);
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  }, [leaderboardTimeframe]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const shareViaWhatsApp = () => {
    const message = `Join DIIT Computers and learn coding! Use my referral code: ${referralInfo.referral_code}\n\nRegister here: ${referralInfo.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaTelegram = () => {
    const message = `Join DIIT Computers and learn coding! Use my referral code: ${referralInfo.referral_code}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(referralInfo.referral_link)}&text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleWithdraw = async (points) => {
    if (!confirm(`Withdraw ${points} points for rewards?`)) return;
    
    setWithdrawing(true);
    try {
      await apiClient.post("/referrals/withdraw", { points });
      toast.success("Withdrawal request submitted!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'text-warning';
      case 'approved': 
      case 'completed':
      case 'credited': return 'text-accent';
      case 'rejected': return 'text-danger';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return Clock;
      case 'approved':
      case 'completed':
      case 'credited': return CheckCircle;
      case 'rejected': return XCircle;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const availablePoints = referralInfo?.available_points || 0;
  const nextTier = referralInfo?.withdrawal_tiers?.find(t => t > availablePoints) || referralInfo?.withdrawal_tiers?.[referralInfo.withdrawal_tiers.length - 1];
  const progress = nextTier ? (availablePoints / nextTier) * 100 : 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Gift weight="fill" className="w-5 h-5 text-primary" />
            <h1 className="font-heading font-semibold">Referral Program</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Points Card */}
        <div className="bg-gradient-to-br from-primary/20 via-surface to-accent/20 rounded-xl p-6 border border-primary/30">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Available Points</p>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold text-primary">{availablePoints}</span>
                <span className="text-muted-foreground">points</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {referralInfo?.total_referrals || 0} students referred • {referralInfo?.points_per_referral || 50} pts each
              </p>
            </div>
            <div className="text-left md:text-right w-full md:w-auto">
              <p className="text-sm text-muted-foreground mb-1">Progress to {nextTier} pts</p>
              <Progress value={Math.min(progress, 100)} className="w-full md:w-48 h-2 mb-2" />
              <p className="text-xs text-muted-foreground">
                {Math.max(0, nextTier - availablePoints)} more points needed
              </p>
            </div>
          </div>
        </div>

        {/* Referral Code & Share */}
        <div className="bg-surface rounded-xl p-6 border border-border/40">
          <h2 className="font-heading font-semibold mb-4">Your Referral Code</h2>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border">
                <code className="flex-1 font-mono text-lg text-primary font-bold tracking-wider">
                  {referralInfo?.referral_code}
                </code>
                <button
                  onClick={() => copyToClipboard(referralInfo?.referral_code, "Code")}
                  className="p-2 hover:bg-surface rounded-lg transition-colors"
                  data-testid="copy-code-button"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border">
                <span className="flex-1 text-sm truncate text-muted-foreground">
                  {referralInfo?.referral_link}
                </span>
                <button
                  onClick={() => copyToClipboard(referralInfo?.referral_link, "Link")}
                  className="p-2 hover:bg-surface rounded-lg transition-colors"
                  data-testid="copy-link-button"
                >
                  <LinkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <span className="text-sm text-muted-foreground">Share via:</span>
            <button
              onClick={shareViaWhatsApp}
              className="p-2 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-colors"
              data-testid="share-whatsapp"
            >
              <WhatsappLogo weight="fill" className="w-6 h-6" />
            </button>
            <button
              onClick={shareViaTelegram}
              className="p-2 rounded-lg bg-[#0088cc]/20 text-[#0088cc] hover:bg-[#0088cc]/30 transition-colors"
              data-testid="share-telegram"
            >
              <TelegramLogo weight="fill" className="w-6 h-6" />
            </button>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Join DIIT Computers',
                    text: `Use my referral code: ${referralInfo?.referral_code}`,
                    url: referralInfo?.referral_link
                  });
                } else {
                  copyToClipboard(referralInfo?.referral_link, "Link");
                }
              }}
              className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
              data-testid="share-native"
            >
              <Share weight="fill" className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Withdraw", icon: Gift },
            { id: "history", label: "Referrals", icon: Users },
            { id: "withdrawals", label: "Withdrawals", icon: Coins },
            { id: "leaderboard", label: "Leaderboard", icon: Trophy }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-surface text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="bg-surface rounded-xl p-6 border border-border/40">
            <h3 className="font-heading font-semibold mb-4">Withdraw Rewards</h3>
            <p className="text-muted-foreground mb-6">
              Redeem your referral points for exciting rewards! Choose a withdrawal tier below.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {referralInfo?.withdrawal_tiers?.map((tier, idx) => {
                const canWithdraw = availablePoints >= tier;
                const hasPending = withdrawalHistory.some(w => w.status === 'pending');
                const rewards = [
                  "Certificate + Swag",
                  "Course Discount 20%",
                  "Course Discount 30%",
                  "Free Advanced Course"
                ];
                
                return (
                  <div
                    key={tier}
                    className={`p-4 rounded-xl border transition-all ${
                      canWithdraw && !hasPending
                        ? "border-primary/50 bg-primary/5 hover:bg-primary/10"
                        : "border-border/40 opacity-60"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-heading text-2xl font-bold">{tier}</span>
                      <Coins className={`w-6 h-6 ${canWithdraw ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{rewards[idx]}</p>
                    <Button
                      onClick={() => handleWithdraw(tier)}
                      disabled={!canWithdraw || withdrawing || hasPending}
                      className={`w-full rounded-full ${
                        canWithdraw && !hasPending
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "bg-muted text-muted-foreground"
                      }`}
                      data-testid={`withdraw-${tier}`}
                    >
                      {hasPending ? "Pending Request" : canWithdraw ? "Withdraw" : `Need ${tier - availablePoints} more`}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="p-4 border-b border-border/40">
              <h3 className="font-heading font-semibold">Referral History</h3>
              <p className="text-sm text-muted-foreground">Students who joined using your code</p>
            </div>
            
            {referralHistory.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No referrals yet. Share your code to start earning!</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {referralHistory.map(ref => {
                  const StatusIcon = getStatusIcon(ref.status);
                  return (
                    <div key={ref.id} className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/70 to-accent/70 flex items-center justify-center text-sm font-bold text-background">
                        {ref.referred_user_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{ref.referred_user_name}</p>
                        <p className="text-sm text-muted-foreground">{formatDate(ref.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 ${getStatusColor(ref.status)}`}>
                          <StatusIcon weight="fill" className="w-4 h-4" />
                          <span className="text-sm capitalize">{ref.status}</span>
                        </div>
                        <p className="font-mono text-sm text-accent">+{ref.points_earned} pts</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "withdrawals" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="p-4 border-b border-border/40">
              <h3 className="font-heading font-semibold">Withdrawal History</h3>
              <p className="text-sm text-muted-foreground">Your reward redemption requests</p>
            </div>
            
            {withdrawalHistory.length === 0 ? (
              <div className="p-8 text-center">
                <Coins className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No withdrawals yet. Earn points and redeem rewards!</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {withdrawalHistory.map(withdrawal => {
                  const StatusIcon = getStatusIcon(withdrawal.status);
                  return (
                    <div key={withdrawal.id} className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        withdrawal.status === 'completed' ? 'bg-accent/20' :
                        withdrawal.status === 'pending' ? 'bg-warning/20' :
                        withdrawal.status === 'rejected' ? 'bg-danger/20' : 'bg-primary/20'
                      }`}>
                        <StatusIcon weight="fill" className={`w-5 h-5 ${getStatusColor(withdrawal.status)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{withdrawal.points} Points Withdrawal</p>
                        <p className="text-sm text-muted-foreground">{formatDate(withdrawal.created_at)}</p>
                        {withdrawal.admin_notes && (
                          <p className="text-xs text-muted-foreground mt-1">Note: {withdrawal.admin_notes}</p>
                        )}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm capitalize ${
                        withdrawal.status === 'completed' ? 'bg-accent/20 text-accent' :
                        withdrawal.status === 'pending' ? 'bg-warning/20 text-warning' :
                        withdrawal.status === 'rejected' ? 'bg-danger/20 text-danger' :
                        'bg-primary/20 text-primary'
                      }`}>
                        {withdrawal.status}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "leaderboard" && (
          <div className="bg-surface rounded-xl border border-border/40 overflow-hidden">
            <div className="p-4 border-b border-border/40">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Activity Leaderboard</h3>
                  <p className="text-sm text-muted-foreground">Ranking from referrals, posts, likes, comments, and admin bonus points</p>
                </div>
                <div className="flex items-center gap-2">
                  {[
                    { id: "weekly", label: "Weekly" },
                    { id: "monthly", label: "Monthly" },
                    { id: "all", label: "All-time" }
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setLeaderboardTimeframe(option.id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        leaderboardTimeframe === option.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`leaderboard-timeframe-${option.id}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {leaderboard.length === 0 ? (
              <div className="p-8 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No users available for this timeframe yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {leaderboard.map(entry => (
                  <div 
                    key={entry.user_id} 
                    className={`p-4 flex items-center gap-4 ${entry.is_current_user ? 'bg-primary/5' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      entry.rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                      entry.rank === 2 ? 'bg-gray-400/20 text-gray-400' :
                      entry.rank === 3 ? 'bg-amber-600/20 text-amber-600' :
                      'bg-surface-elevated text-muted-foreground'
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/70 to-accent/70 flex items-center justify-center text-sm font-bold text-background">
                      {entry.full_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {entry.full_name}
                        {entry.is_current_user && <span className="text-primary ml-2">(You)</span>}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {entry.total_referrals} referrals • {entry.total_posts} posts • {entry.total_likes_received} likes • {entry.total_comments_received} comments • {entry.score_breakdown?.bonus_marks ?? entry.bonus_marks ?? 0} bonus
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-primary">{entry.total_marks ?? entry.total_points}</p>
                      <p className="text-xs text-muted-foreground">score</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* How it works */}
        <div className="bg-surface rounded-xl p-6 border border-border/40">
          <h3 className="font-heading font-semibold mb-4">How It Works</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <Share className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-semibold mb-1">1. Share</h4>
              <p className="text-sm text-muted-foreground">Share your unique referral code with friends</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <h4 className="font-semibold mb-1">2. They Join</h4>
              <p className="text-sm text-muted-foreground">Friends register using your code</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center mx-auto mb-3">
                <Gift className="w-6 h-6 text-warning" />
              </div>
              <h4 className="font-semibold mb-1">3. Earn Rewards</h4>
              <p className="text-sm text-muted-foreground">Get {referralInfo?.points_per_referral || 50} points per referral, redeem at tiers</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
